import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import * as tokens from '@nicolaudie-group/ng-ui-kit/helpers/tokens'
import redirect from '@nicolaudie-group/ng-ui-kit/helpers/redirect'
import { isError } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import isIn from '@nicolaudie-group/ng-ui-kit/helpers/isIn'
import trim from '@nicolaudie-group/ng-ui-kit/helpers/trim'

import * as sdk from '@/helpers/sdk'
import translate from '@/helpers/translate'

import config from '@/config'

import useSettingsStore from '@/stores/settings.store'
import useRootStore from '@/stores/root.store'
// Circular dependencies are possible with Pinia, with caveats: https://pinia.vuejs.org/cookbook/composing-stores.html#Composing-Stores
import useProfileStore from '@/stores/profile.store'
import PbError from '@/classes/PbError.class'
import useAction from '@/composables/useAction'
import useSupportStore from './support.store'
import isOnline from '@/helpers/isOnline'

/**
 * Allows disabling redirection to login/logout for debugging purposes
 */
const DISABLE_LOGOUT_REDIRECT = false

const logoutEnabled = !DISABLE_LOGOUT_REDIRECT || !isIn.development

/**
 * Cached token to check for changes
 * in "checkToken"
 */
let cachedToken = tokens.get()
let failedToken = false

const parseGoto = (goto) => {
  let result = ''
  if (typeof goto === 'string') {
    result = goto
  } else if (goto?.fullPath) {
    result = goto.fullPath
  }
  return result
}

const buildUrl = (path, queries = {}) => {
  const queryArr = []
  Object.map(queries, (key, value) => {
    queryArr.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  })
  // Trim leading/trailing slashes + split path
  const url = Array.isArray(path) ? path : trim(path, '/').split('/')
  let result = url.join('/')
  if (queryArr.length) {
    result += `?${queryArr.join('&')}`
  }
  return result
}

export const userKey = 'data'
export const userStatus = 'status'

const useUserStore = defineStore('user', () => {
  // Translations/Language
  const language = ref(null)
  async function getTranslation(languageCode) {
    // Only reload translations if the language code changed
    if (languageCode !== language.value) {
      language.value = null
      try {
        // await translate.setLanguage(this.route.query?.lgCode || userStore.data.languageCode)
        await translate.setLanguage(languageCode)
      } catch (err) {
        throw new PbError({
          title: 'Failed to download translation',
          message: 'An error occured while downloading the translation file',
          page: true,
          cause: err,
        })
      }
    }
    return languageCode
  }

  const {
    action: setLanguage,
    status: translationStatus,
  } = useAction(getTranslation, { data: language })

  const {
    action: getInfos,
    data: apiInfos,
  } = useAction(async () => (
    (await sdk.v2.core.getInfos()).data
  ), { shallow: true })

  async function getUser() {
    const settingsStore = useSettingsStore()
    /**
     * WARNING: there is a circular dependency, observe the following recommendations:
     * https://pinia.vuejs.org/cookbook/composing-stores.html#Composing-Stores
     */
    const profileStore = useProfileStore()
    const supportStore = useSupportStore()
    // Retrieve basic infos from the API
    await getInfos()
    const userUid = apiInfos.value?.user?.uid
    if (!userUid) {
      throw new PbError({
        title: 'Unable to identify user',
        message: 'User data cannot be loaded',
      })
    }
    // Get the user and wait for the response
    let result
    try {
      result = await sdk.v2.users.get({ url: { userUid } })
    } catch (error) {
      setLanguage('en')
      throw error
    }
    // Start loading other initial data in parallel (without await)
    settingsStore.getGroups()
    profileStore.getBrands()
    profileStore.getConstants()
    supportStore.loadSession()
    // Set the language based on user preferences to start translations downloaded
    setLanguage(result.data.languageCode)
    // Return the user
    return result.data
  }

  const { action: get, data, status } = useAction(getUser)

  const groups = computed(() => {
    const userGroups = data.value?.groups
    return Array.isArray(userGroups) ? userGroups : []
  })

  const role = computed(() => data.value?.role || null)

  /**
   * Returns wether the user is in the "_admin" group or not
   * @returns {boolean}
   */
  const isAdmin = computed(() => groups.value.includes(config.groups.admin))

  /**
   * Returns wether the user is in the "_sslManager" group or not
   * @returns {boolean}
   */
  const isSSLManager = computed(() => groups.value.includes(config.groups.SSLManager))

  /**
   * Returns wether the user has the "internal" role or not
   * @returns {boolean}
   */
  const isInternal = computed(() => role.value === config.roles.internal)

  /**
   * Returns wether the user can manage SSLs or not (admin or sslManager)
   * @returns {boolean}
   */
  const canManageProfiles = computed(() => {
    const admin = isAdmin.value
    const sslManager = isSSLManager.value
    return admin || sslManager
  })

  function clearLoginData() {
    if (logoutEnabled && isOnline()) {
      // Remove the tokens
      tokens.remove()
      // Clear the cached token
      cachedToken = null
      // Clear the user data
      data.value = null
    }
  }

  /**
   * Redirect to SSO Login page and handle tokens
   */
  function goToLogin({ goto, time } = {}) {
    clearLoginData()
    const path = [config.url, 'backend', 'signin', config.clientUid]
    const queries = {
      goto: parseGoto(goto),
      redirectUrl: buildUrl([config.url, 'backend', 'redirect', config.clientUid]),
      state: '',
    }
    const location = buildUrl(path, queries)
    dev.log(`Redirecting to login ${location} in ${time || 0}ms`)
    if (logoutEnabled  && isOnline()) {
      return redirect(location, time)
    }
    return undefined
  }

  /**
   * Redirect to SSO Logout page and handle tokens
   */
  function goToLogout({ goto, time, keepSession } = {}) {
    clearLoginData()
    const path = [config.url, 'backend', 'signout', config.clientUid]
    /*
    "keepSession = false" when:
      - user clicked "logout" button
      - token was invalid right after login
    "keepSession = true"  when:
      - user clicked "switch account" button
      - token expired when checking periodically ?
      - token expired when checking on route change ?
      - token expired when loading initially ?
    */
    const queries = {
      goto: parseGoto(goto),
      redirectUrl: buildUrl([config.url, 'backend', 'redirect', config.clientUid]),
      state: '',
      keepSession: Boolean.from(keepSession),
    }
    const location = buildUrl(path, queries)
    dev.log(`Redirecting to logout ${location} in ${time || 0}ms`)
    if (logoutEnabled && isOnline()) {
      return redirect(location, time)
    }
    return undefined
  }

  /**
   * Check and store the token
   * @param {string} token - Token to check and store
   * @returns {Promise} Decoded token
   */
  async function checkToken(token) {
    const rootStore = useRootStore()
    // If the token is set
    let decoded = null
    try {
      decoded = tokens.check(token)
    } catch (error) {
      // If the token is not valid
      // Log the error, but only if we didn't already have an invalid token before
      if (!failedToken) {
        rootStore.addError(new PbError({
          ...translate('pb2.errors.invalidToken'),
          message: error.message,
          cause: error,
        }))
      }
    }

    if (decoded) {
      // If the token was valid
      failedToken = false

      const tokenChanged = token !== cachedToken
      if (tokenChanged) {
        // If the token changed, save it
        tokens.set(token)
      }
      if (
        tokenChanged
        || (!data.value && !isError(status.value))
      ) {
        // If the token changed
        // OR the user is not loaded (and not because of an error)
        // Reload the user
        const user = await get()
        if (!user) {
          // If the user still could not load, assume the token is invalid
          return false
        }
      }
      // Cache the new token
      cachedToken = token
      // Return that the token was valid
      return true
    }

    failedToken = true

    // Return that the token was invalid (not set, expired...)
    return false
  }

  return {
    // Translations/Language
    setLanguage,
    language,
    translationStatus,
    // User
    apiInfos,
    get,
    data,
    status,
    // Session management
    goToLogin,
    goToLogout,
    checkToken,
    // User metadata
    groups,
    role,
    isAdmin,
    isSSLManager,
    isInternal,
    canManageProfiles,
  }
})

export default useUserStore
